import { doc, setDoc } from 'firebase/firestore';
import { db } from '@/firebaseConfig';
import { telemetry } from '@/lib/telemetry';
import {
  MicrosoftGraphApiClient,
  createGraphClient,
} from '../services/graphApiClient';

// Check if we're in development mode
export const shouldUseMockSharePoint =
  process.env.NODE_ENV === 'development' &&
  import.meta.env.VITE_FORCE_REAL_MSAL_ENV !== 'true';

// Mock drive ID to use in development
const MOCK_DRIVE_ID = 'mock-drive-id';

/**
 * Gets the SharePoint drive ID directly from Microsoft Graph API
 * No drive IDs are stored in Firestore, only a flag indicating if SharePoint is connected
 *
 * @param accessToken - Microsoft Graph API access token
 * @returns The SharePoint drive ID as a string
 * @throws Error if the drive ID cannot be retrieved
 */
export async function getSharePointDriveId(
  clientOrAccessToken: MicrosoftGraphApiClient | string,
): Promise<string> {
  if (shouldUseMockSharePoint) {
    return MOCK_DRIVE_ID;
  }

  const client =
    typeof clientOrAccessToken === 'string'
      ? createGraphClient(clientOrAccessToken)
      : clientOrAccessToken;

  try {
    // First, get the root SharePoint site
    const rootSiteResponse = await client.fetch('/sites/root');

    if (!rootSiteResponse.ok) {
      const errorData = await rootSiteResponse.json();
      throw new Error(
        `Failed to get SharePoint site: ${
          errorData.error?.message || rootSiteResponse.statusText
        }`,
      );
    }

    const rootSiteData = await rootSiteResponse.json();

    // Then get the default document library (drive)
    const drivesResponse = await client.fetch(
      `/sites/${rootSiteData.id}/drives`,
    );

    if (!drivesResponse.ok) {
      const errorData = await drivesResponse.json();
      throw new Error(
        `Failed to get SharePoint drives: ${
          errorData.error?.message || drivesResponse.statusText
        }`,
      );
    }

    const drivesData = await drivesResponse.json();

    // Get the default document library (usually named "Documents")
    const defaultDrive = drivesData.value.find(
      (drive: any) => drive.name === 'Documents',
    );

    if (!defaultDrive || !defaultDrive.id) {
      throw new Error(
        'No default document library found in your SharePoint site',
      );
    }

    return defaultDrive.id;
  } catch (error) {
    telemetry.error({
      error,
      message: 'Error getting SharePoint drive ID.',
    });
    throw error instanceof Error
      ? error
      : new Error('Failed to get SharePoint drive ID');
  }
}

/**
 * Marks a firm as connected to SharePoint
 * Only stores a boolean flag in Firestore, not the actual drive ID
 *
 * @param firmId - The ID of the firm to update
 * @returns Promise that resolves when the operation completes
 */
export async function markFirmAsSharePointConnected(
  firmId: string,
): Promise<void> {
  if (!firmId) return;

  try {
    // For development mode, do nothing
    if (shouldUseMockSharePoint) {
      return;
    }

    // Update the firm document in Firestore
    const firmRef = doc(db, 'firms', firmId);
    await setDoc(
      firmRef,
      {
        integration: {
          sharePointConnected: true,
          connectedAt: new Date().toISOString(),
        },
      },
      { merge: true },
    );
  } catch (error) {
    telemetry.error({
      error,
      message: 'Error marking firm as SharePoint connected.',
      extra: { firmId },
    });
    throw error instanceof Error
      ? error
      : new Error('Failed to update SharePoint connection status');
  }
}
